import React from 'react'
import styled from '@emotion/styled'

import {cssTheme} from '@vio/atlas/themes'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

const TaxDisplay = styled.span`
  display: inline-block;
  color: ${cssTheme.colors.content.neutral.secondary};
  ${cssTheme.typography.bodyXS};
`

const Separator = styled.span`
  padding: 0 ${cssTheme.layout.spacing.s200};
`

interface Props {
  /** Pass through id to allow performance tracking */
  id?: string
  /** Pass through classname to allow styles overrides */
  className?: string
  /** If the users search is for more than one room */
  includeTaxes: boolean
  /** If supplied it will add the currency code to the message */
  currencyCode?: string
  /** If supplied it will show the PriceMessage */
  showPriceMessage?: boolean
  /** If supplied it will show the total price message */
  showTotalPrices?: boolean
  /** If supplied it will show the additional charges message - used for Israel compliance */
  withAdditionalCharges?: boolean
  /** Whether the booking is split */
  isSplitBooking?: boolean // Added as part of 32cf4c25-srp-room-count-copy experiment
  /** The number of rooms for the offer, used to display the correct message */
  numberOfRooms?: number // Added as part of 32cf4c25-srp-room-count-copy experiment
  /** The number of nights for the offer, used to display the correct message */
  numberOfNights?: number // Added as part of 32cf4c25-srp-room-count-copy experiment
}
export const BaseTaxationAndPriceMessage = ({
  id,
  includeTaxes,
  currencyCode,
  className,
  showPriceMessage = true,
  showTotalPrices = false,
  withAdditionalCharges = false,
  isSplitBooking = false,
  numberOfRooms,
  numberOfNights
}: Props) => {
  const toggle = useToggle()

  const hasRoomCount = !isSplitBooking && Boolean(numberOfRooms)
  const hasNightCount = showTotalPrices && Boolean(numberOfNights)

  const shouldShowRoomCount =
    (hasRoomCount || hasNightCount) &&
    toggle('32cf4c25-srp-room-count-copy', false, true)

  const shouldHideMessage =
    !showPriceMessage &&
    !includeTaxes &&
    !currencyCode &&
    !withAdditionalCharges &&
    !shouldShowRoomCount

  if (shouldHideMessage) return null

  return (
    <TaxDisplay id={id} className={className} data-id="TaxationAndPriceMessage">
      {showPriceMessage && (
        <span>
          {showTotalPrices ? (
            <FormattedMessageWrapper
              id="taxation.totalPrice"
              defaultMessage="total price"
            />
          ) : (
            <FormattedMessageWrapper
              id="price.nightly"
              defaultMessage="nightly price"
            />
          )}
        </span>
      )}

      {shouldShowRoomCount && (
        <>
          {showPriceMessage && <Separator>·</Separator>}
          {!isSplitBooking && (
            <FormattedMessageWrapper
              id="numberOfRooms"
              defaultMessage="{count, plural, one {1 room} other {# rooms}}"
              values={{
                count: numberOfRooms
              }}
            />
          )}
          {numberOfNights && showTotalPrices && (
            <>
              {!isSplitBooking && <span>,&nbsp;</span>}
              <FormattedMessageWrapper
                id="numberOfNights"
                defaultMessage="{count, plural, one {1 night} other {# nights}}"
                values={{count: numberOfNights}}
              />
            </>
          )}
          <br />
        </>
      )}

      {includeTaxes && !withAdditionalCharges && (
        <>
          {(showPriceMessage && <>&nbsp;</>) || null}
          <FormattedMessageWrapper
            id="taxation.withTaxesAndFees"
            defaultMessage="with taxes & fees"
          />
        </>
      )}
      {currencyCode && (
        <>
          <>&nbsp;</>
          <FormattedMessageWrapper
            id="inCurrency"
            defaultMessage="in {currencyCode}"
            values={{currencyCode}}
          />
        </>
      )}
      {withAdditionalCharges && (
        <>
          {showPriceMessage && <br />}
          <TaxDisplay>
            <FormattedMessageWrapper
              id="payment.additionalCharges"
              defaultMessage="Additional charges may apply"
            />
          </TaxDisplay>
        </>
      )}
    </TaxDisplay>
  )
}
